import type { ClaimResponsibilityType, ProviderClaimResponsibilityReportItem } from '@enter/core';
import {
    mdiAlertCircleOutline,
    mdiAt,
    mdiCancel,
    mdiCashCheck,
    mdiCashRefund,
    mdiCodeBraces,
    mdiCommentTextOutline,
    mdiEmailOutline,
    mdiExclamationThick,
    mdiFax,
    mdiHelpCircleOutline,
    mdiInboxArrowDown,
    mdiInboxOutline,
    mdiPauseCircleOutline,
    mdiPhoneOutline,
    mdiScaleBalance,
    mdiSendOutline,
    mdiSwapHorizontal,
    mdiTagOutline,
    mdiTrayArrowUp,
    mdiWalletOutline,
} from '@mdi/js';

/**
 * Derivations for the "Active Claims By Responsibility" widget on the provider Claims page (FE-151).
 *
 * Fed by `GET /provider/report/responsibility`: one row per (responsibility type, category type)
 * pair with a distinct-claim count and summed charges. Each claim sits in exactly one pair, so
 * counts and charges add up across rows. Charges are summed in integer cents to avoid float drift.
 *
 * Kept free of React and the DOM so it runs under `jest.services.config.js`.
 */

export type ResponsibilityType = `${ClaimResponsibilityType}`;
export type ResponsibilityMetric = 'charges' | 'count';

/** NONE is "complete": shown as its own pill, never in the main bar or its shares. */
export const NONE_RESPONSIBILITY: ResponsibilityType = 'NONE';
export const UNKNOWN_RESPONSIBILITY: ResponsibilityType = 'UNKNOWN';
export const UNKNOWN_CATEGORY = 'UNKNOWN';

const RESPONSIBILITY_TYPES: ReadonlyArray<ResponsibilityType> = ['ENTER', 'PAYER', 'PROVIDER', 'GUARANTOR', 'NONE', 'UNKNOWN'];

export interface IResponsibilityTotals {
    count: number;
    chargeCents: number;
}

export interface IResponsibilityCategoryTotal extends IResponsibilityTotals {
    /** Raw enum code. Kept as a string: a category the client enum does not know yet still renders. */
    code: string;
}

export interface IResponsibilityGroupTotal extends IResponsibilityTotals {
    type: ResponsibilityType;
    categories: IResponsibilityCategoryTotal[];
}

export interface IResponsibilitySummary {
    /** Only types the API returned at least one row for — no zero-value placeholders. */
    groups: Partial<Record<ResponsibilityType, IResponsibilityGroupTotal>>;
    /** Every group except NONE, in no particular order. Rank with `rankByMetric`. */
    activeWork: IResponsibilityGroupTotal[];
    none?: IResponsibilityGroupTotal;
    /** All groups including NONE — the header figures. */
    total: IResponsibilityTotals;
    /** All groups except NONE — the table total when nothing is selected. */
    activeWorkTotal: IResponsibilityTotals;
}

/** "1234.56" → 123456. Missing or unparsable amounts count as zero, as the report itself does. */
export function toCents(amount?: string | null): number {
    const value = parseFloat(amount ?? '');
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
}

function toResponsibilityType(value: unknown): ResponsibilityType {
    return RESPONSIBILITY_TYPES.includes(value as ResponsibilityType) ? (value as ResponsibilityType) : UNKNOWN_RESPONSIBILITY;
}

function sumTotals(items: IResponsibilityTotals[]): IResponsibilityTotals {
    return items.reduce((acc, x) => ({ count: acc.count + x.count, chargeCents: acc.chargeCents + x.chargeCents }), {
        count: 0,
        chargeCents: 0,
    });
}

export function summarizeResponsibilityReport(items: ProviderClaimResponsibilityReportItem[]): IResponsibilitySummary {
    const groups: Partial<Record<ResponsibilityType, IResponsibilityGroupTotal>> = {};

    items.forEach(item => {
        const type = toResponsibilityType(item.claimResponsibilityType);
        const code = item.claimResponsibilityCategoryType || UNKNOWN_CATEGORY;
        const count = Number(item.count) || 0;
        const chargeCents = toCents(item.chargeAmount?.amount);

        const group = (groups[type] ??= { type, count: 0, chargeCents: 0, categories: [] });
        group.count += count;
        group.chargeCents += chargeCents;

        // The contract is one row per pair, but merge rather than render a duplicate chip if it is not.
        const category = group.categories.find(c => c.code === code);
        if (category) {
            category.count += count;
            category.chargeCents += chargeCents;
        } else {
            group.categories.push({ code, count, chargeCents });
        }
    });

    const all = Object.values(groups) as IResponsibilityGroupTotal[];
    const activeWork = all.filter(g => g.type !== NONE_RESPONSIBILITY);

    return {
        groups,
        activeWork,
        none: groups[NONE_RESPONSIBILITY],
        total: sumTotals(all),
        activeWorkTotal: sumTotals(activeWork),
    };
}

export function metricValue(totals: IResponsibilityTotals, metric: ResponsibilityMetric): number {
    return metric === 'count' ? totals.count : totals.chargeCents;
}

export interface IRankedShare<T> {
    item: T;
    /** 0–100, against the sum of the ranked items. 0 when that sum is 0. */
    share: number;
}

/**
 * Sort descending by the selected metric and compute each item's share of the sum.
 * Ties fall back to the other metric so the order does not flicker between renders.
 */
export function rankByMetric<T extends IResponsibilityTotals>(items: T[], metric: ResponsibilityMetric): IRankedShare<T>[] {
    const other: ResponsibilityMetric = metric === 'count' ? 'charges' : 'count';
    const denominator = items.reduce((sum, x) => sum + metricValue(x, metric), 0);

    return [...items]
        .sort((a, b) => metricValue(b, metric) - metricValue(a, metric) || metricValue(b, other) - metricValue(a, other))
        .map(item => ({ item, share: denominator > 0 ? (metricValue(item, metric) / denominator) * 100 : 0 }));
}

/** One decimal below 1% so a sliver is not shown as 0%; whole percent otherwise. */
export function formatShare(share: number): string {
    // A $265 category beside $31M of active work is 0.0009% — "0.0%" would read as nothing.
    if (share > 0 && share < 0.05) {
        return '<0.1%';
    }
    if (share > 0 && share < 1) {
        return `${share.toFixed(1)}%`;
    }
    return `${Math.round(share)}%`;
}

const integerFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export function formatClaimCount(count: number): string {
    return integerFormat.format(count);
}

/**
 * Compact money for the widget: `$0`, `$265`, `$42K`, `$49.46M`.
 *
 * The design rounds everything under $1M to thousands, which shows a $265 category as `$0K`.
 * Below $1K this shows whole dollars instead (crosswalk §10 open item).
 */
export function formatCompactMoney(cents: number): string {
    if (!cents) {
        return '$0';
    }
    const sign = cents < 0 ? '-' : '';
    const dollars = Math.abs(cents) / 100;

    // Thresholds compare the ROUNDED value, so $999,600 reads "$1.00M" rather than "$1000K".
    if (Math.round(dollars / 1e3) >= 1000) {
        return `${sign}$${(Math.round(dollars / 1e4) / 100).toFixed(2)}M`;
    }
    if (Math.round(dollars) >= 1000) {
        return `${sign}$${Math.round(dollars / 1e3)}K`;
    }
    if (Math.round(dollars) === 0) {
        return `${sign}<$1`;
    }
    return `${sign}$${Math.round(dollars)}`;
}

const CATEGORY_LABEL_OVERRIDES: Record<string, string> = {
    CLAIM_CLOSEOUT: 'Closeout',
    TRANSMISSION_REJECTED_OR_DENIED: 'Rejected / denied',
    PROFESSIONAL_CREATED: 'Professional created (unsubmitted)',
    INSTITUTIONAL_CREATED: 'Institutional created (unsubmitted)',
    CLAIM_HALT: 'Claim halted',
    HL7_RECEIVED: 'HL7 received',
    UNKNOWN: 'Unknown',
};

/** `TRANSMISSION_WITHIN_45_DAYS` → "Transmission ≤45d", `GUARANTOR_COLLECTIONS` → "Guarantor collections". */
export function categoryLabel(code: string): string {
    if (CATEGORY_LABEL_OVERRIDES[code]) {
        return CATEGORY_LABEL_OVERRIDES[code];
    }
    const text = code
        .replace(/_(WITHIN|BEYOND)_(\d+)_DAYS$/, (_m, window: string, days: string) => ` ${window === 'WITHIN' ? '≤' : '>'}${days}d`)
        .replace(/_/g, ' ')
        .toLowerCase()
        .trim();
    return text ? text[0].toUpperCase() + text.slice(1) : CATEGORY_LABEL_OVERRIDES.UNKNOWN;
}

/** First substring match wins, so the order matters: REJECTED before TRANSMISSION, FAX before RECEIVED. */
const CATEGORY_ICONS: ReadonlyArray<[string, string]> = [
    ['CLOSEOUT', mdiScaleBalance],
    ['REJECTED', mdiCancel],
    ['CROSSOVER', mdiSwapHorizontal],
    ['TRANSMISSION', mdiSendOutline],
    ['LETTER', mdiEmailOutline],
    ['FAX', mdiFax],
    ['PHONE', mdiPhoneOutline],
    ['EMAIL', mdiAt],
    ['CODING', mdiCodeBraces],
    ['COMMENT', mdiCommentTextOutline],
    ['TAG', mdiTagOutline],
    ['PAID', mdiCashCheck],
    ['CREATED', mdiTrayArrowUp],
    ['COLLECTIONS', mdiWalletOutline],
    ['OVERPAYMENT', mdiCashRefund],
    ['ESCALATION', mdiExclamationThick],
    ['WORKER', mdiAlertCircleOutline],
    ['HALT', mdiPauseCircleOutline],
    ['EMPTY', mdiInboxOutline],
    ['RECEIVED', mdiInboxArrowDown],
];

/** MDI path for a category code; help icon when nothing matches. */
export function categoryIcon(code: string): string {
    return CATEGORY_ICONS.find(([fragment]) => code.includes(fragment))?.[1] ?? mdiHelpCircleOutline;
}

/**
 * How far a category's swatch sits from its group colour toward white, by rank:
 * 100% (the group colour) for the largest, stepping down to a 30% floor for the smallest.
 */
export function categoryShadePercent(rank: number, count: number): number {
    return Math.max(100 - rank * (70 / Math.max(count - 1, 1)), 30);
}
